import Cache from '@fs/zion-cache'
import { mutate } from 'swr'
import type { CoupleId, FamilyMemberChild, PersonId } from '@fs/zion-tree-types'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import type { SwrResponse } from './useSwrRequest'
import { useSwrRequest } from './useSwrRequest'

const cache = new Cache({ storeName: 'children_r9', encrypted: process.env.NODE_ENV === 'production' })

const CHILDREN_PATH = '/service/tree/tree-data/r9/family-members/couple/'

function swrKey(coupleId: CoupleId, pid: PersonId): string {
  const focusPersonQuery = pid ? `focusPersonId=${pid}` : undefined
  const queries = [focusPersonQuery, 'includePhotos=true'].filter(Boolean).join('&')
  return `${CHILDREN_PATH}${coupleId}/children?${queries}`
}
export default function useChildren(
  coupleId: CoupleId | undefined,
  initialValue: FamilyMemberChild[] | undefined
): SwrResponse<FamilyMemberChild[]> & { children?: FamilyMemberChild[] } {
  const { pid } = usePersonPageParams('full')
  const response = useSwrRequest<FamilyMemberChild[]>(coupleId ? swrKey(coupleId, pid) : null, cache, {
    fallbackData: initialValue,
    // This will prevent it from making a call to the server on mount IF we already have the children passed in as initialValue
    revalidateOnMount: !initialValue,
    // This is a piece of data we don't really need to revalidate on refocus, since it would trigger a call for every spouse and set of parents (that have their children list open)
    revalidateOnFocus: false,
  })
  return { ...response, children: response.data }
}
export function mutateChildren(coupleId: CoupleId, pid: PersonId): Promise<void> {
  return mutate(swrKey(coupleId, pid))
}

/**
 * Revalidates every cached children list. Unlike mutateChildren, the caller does not need to know
 * which couples are expanded — each has its own key, and any number can be open.
 */
export function mutateAllChildren(): Promise<unknown> {
  return mutate((key) => typeof key === 'string' && key.startsWith(CHILDREN_PATH))
}
